import React, { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import PageLoader from "components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiLock, FiTrash2, FiStar, FiUserPlus } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import { Modal } from "@beleef/ui";
import Table from "components/ui/Table";
import Button from "components/ui/Button";
import {
  useCreateRoleGrantMutation,
  useInviteRoleGrantMutation,
  useGetRoleGrantOptionsQuery,
  useGetRoleGrantsQuery,
  useGetUserAuthorityQuery,
  useUpdateRoleGrantMutation,
  useDeleteRoleGrantMutation,
} from "apis/roleGrant";
import RoleGrantEdit from "components/roleGrant/RoleGrantEdit";
import RoleGrantInvite from "components/roleGrant/RoleGrantInvite";

const ROLE_PILL = {
  owner: "bg-purple-100 text-purple-800",
  admin: "bg-blue-100 text-blue-800",
};

const Pill = ({ label, className }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${className}`}
  >
    {label}
  </span>
);

const RoleGrants = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetRoleGrantsQuery();

  const { data: options, isPending: isOptionsPending } =
    useGetRoleGrantOptionsQuery();

  // Inheritance is resolved server-side, so this is the only way to see that a
  // group grant reaches companies with no row of their own.
  const { data: authority } = useGetUserAuthorityQuery(
    isViewOpen ? selectedUserId : null,
  );

  const { mutate: createGrant, isPending: isCreateLoading } =
    useCreateRoleGrantMutation();
  const { mutate: inviteGrant, isPending: isInviteLoading } =
    useInviteRoleGrantMutation();
  const { mutate: updateGrant, isPending: isUpdateLoading } =
    useUpdateRoleGrantMutation();
  const { mutate: deleteGrant, isPending: isDeleteLoading } =
    useDeleteRoleGrantMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "userName",
        header: "Person",
        cell: (row) => <span>{row.userName || "Unknown user"}</span>,
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "role",
        header: "Role",
        // A scope can hold several owners; the primary is the one invoiced.
        // Labelled rather than left as a bare star, which reads as a rating.
        cell: (row) => (
          <div className="flex items-center gap-1.5">
            <Pill label={row.role} className={ROLE_PILL[row.role]} />
            {row.primary ? (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                title="Primary owner — the invoiced contact"
              >
                <FiStar className="fill-amber-500 text-amber-500" size={12} />
                Primary
              </span>
            ) : null}
          </div>
        ),
        isFilter: true,
        minWidth: "170px",
      },
      {
        accessorKey: "scopeLevel",
        header: "Level",
        cell: (row) => <span className="capitalize">{row.scopeLevel}</span>,
        isFilter: true,
      },
      {
        accessorKey: "scopeName",
        header: "Scope",
        // A grant pointing at something deleted resolves to nothing and reads
        // as a permissions bug, so it is called out rather than left blank.
        cell: (row) =>
          row.scopeName ? (
            <span>{row.scopeName}</span>
          ) : (
            <span className="text-sm text-red-500">Scope no longer exists</span>
          ),
        isFilter: true,
        minWidth: "240px",
      },
      {
        accessorKey: "lastLoginAt",
        header: "Status",
        // Stamped only on web OTP login (controllers/auth/webAuth.js) — the
        // path the invite email actually sends them to. A grant can sit
        // unopened for weeks; this is the only way to tell that apart from
        // someone actively using it.
        cell: (row) =>
          row.lastLoginAt ? (
            <span className="text-sm text-darkgray" title={new Date(row.lastLoginAt).toLocaleString()}>
              Active — last seen {formatDistanceToNow(new Date(row.lastLoginAt), { addSuffix: true })}
            </span>
          ) : (
            <Pill label="Pending" className="bg-amber-100 text-amber-800" />
          ),
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "grantedByName",
        header: "Granted By",
        cell: (row) => (
          <span className="text-sm text-darkgray">
            {row.grantedByName || "Backfill"}
          </span>
        ),
        isFilter: false,
        minWidth: "140px",
      },
      {
        accessorKey: "actions",
        header: "Actions",
        // Platform grants let their holder switch into any agent's account, so
        // they are shown here but only changed by script — the API refuses it.
        cell: (row) =>
          row.scopeLevel === "platform" ? (
            <span
              className="text-darkgray"
              title="Platform grants can't be changed from the dashboard"
            >
              <FiLock fontSize={18} />
            </span>
          ) : (
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsEditOpen(true);
                }}
                className="edit-icon"
                title="Edit Grant"
              >
                <FiEdit2 fontSize={20} />
              </button>
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsDeleteOpen(true);
                }}
                className="delete-icon"
                title="Delete Grant"
              >
                <FiTrash2 fontSize={20} />
              </button>
            </div>
          ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isDataPending || isOptionsPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading grants: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedGrant = data?.find((grant) => grant._id === selectedId);

  const handleCreateSubmit = (formData) => {
    createGrant(formData, { onSuccess: () => setIsAddOpen(false) });
  };

  const handleInviteSubmit = (formData) => {
    inviteGrant(formData, { onSuccess: () => setIsInviteOpen(false) });
  };

  const handleEditSubmit = (formData) => {
    updateGrant(
      { id: selectedId, ...formData },
      { onSuccess: () => setIsEditOpen(false) },
    );
  };

  const handleDelete = () => {
    deleteGrant(selectedId, { onSuccess: () => setIsDeleteOpen(false) });
  };

  const handleViewRowClick = (row) => {
    setSelectedUserId(row.userId);
    setIsViewOpen(true);
  };

  const AddGrantButton = () => (
    <div className="flex items-center gap-2">
      <Button
        className="gray-button flex items-center"
        onClick={() => setIsInviteOpen(true)}
        disabled={isInviteLoading}
      >
        <FiUserPlus size={14} className="mr-2" />
        Invite by Email
      </Button>
      <Button
        className="black-button flex items-center"
        onClick={() => setIsAddOpen(true)}
        disabled={isCreateLoading}
      >
        <FaPlus size={14} className="mr-2" />
        Add Grant
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Authority Grant"
        className="max-w-2xl"
      >
        <RoleGrantEdit
          key={isAddOpen ? "grant-add-open" : "grant-add-closed"}
          initialData={{}}
          options={options}
          onSubmit={handleCreateSubmit}
          isSubmitting={isCreateLoading}
        />
      </Modal>

      <Modal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        title="Invite by Email"
        className="max-w-2xl"
      >
        <RoleGrantInvite
          key={isInviteOpen ? "grant-invite-open" : "grant-invite-closed"}
          options={options}
          onSubmit={handleInviteSubmit}
          isSubmitting={isInviteLoading}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Authority Grant"
        className="max-w-2xl"
      >
        <RoleGrantEdit
          key={selectedId || "grant-edit"}
          initialData={selectedGrant || {}}
          options={options}
          onSubmit={handleEditSubmit}
          isSubmitting={isUpdateLoading}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Grant"
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="black-button"
              loading={isDeleteLoading}
            >
              Delete Grant
            </Button>
          </div>
        }
      >
        <p>
          {selectedGrant?.userName} will lose {selectedGrant?.role} on{" "}
          {selectedGrant?.scopeName}.
        </p>
        <p className="text-sm text-darkgray mt-2">
          {selectedGrant?.primary && selectedGrant?.scopeLevel === "company"
            ? "They are this company's primary owner, so this will be refused. Make another owner primary first."
            : selectedGrant?.primary
              ? "They are the primary owner of this scope, so it will be left with none until another owner is made primary."
              : "This action cannot be undone."}
        </p>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="What This Person Can Reach"
        className="max-w-3xl"
      >
        {!authority ? (
          <p className="text-darkgray">Resolving…</p>
        ) : authority.platform ? (
          <p>
            Everything. This person holds a platform grant, so every network,
            group, company and office is in scope.
          </p>
        ) : (
          <div className="space-y-4">
            {authority.jurisdictions?.length ? (
              <div className="space-y-2">
                <p className="form-label">Jurisdictions</p>
                <div className="flex flex-wrap gap-1">
                  {authority.jurisdictions.map((code) => (
                    <Pill
                      key={code}
                      label={code}
                      className="bg-blue-100 text-blue-800 font-mono"
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {[
              { label: "Groups", rows: authority.groups },
              { label: "Companies", rows: authority.companies },
              {
                label: "Offices",
                rows: (authority.offices || []).map((office) => ({
                  ...office,
                  name: office.suburb
                    ? `${office.name} (${office.suburb})`
                    : office.name,
                })),
              },
            ].map(({ label, rows }) => (
              <div key={label} className="space-y-2">
                <p className="form-label">
                  {label} ({rows?.length || 0})
                </p>
                <p className="text-sm text-darkgray">
                  {rows?.length
                    ? rows.map((row) => row.name).join(", ")
                    : "None"}
                </p>
              </div>
            ))}

            <p className="text-xs text-darkgray border-t border-mediumgray pt-3">
              Resolved through inheritance, so this is wider than the rows in
              the table — a group grant reaches companies with no row of their
              own.
            </p>
          </div>
        )}
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Authority Grants"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddGrantButton}
          emptyText="No grants found"
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default RoleGrants;
